import React, { useCallback, useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { LinkButton } from '@e3dc-react/shell/Elements/Buttons'
import { ReactNode } from 'react-markdown/lib/ast-to-react'
import { useTheme } from '@mui/material'

import { CompanyInstallerTableItemModel, CompanySettingsUserDataModel } from 'src/Libs/company-settings'
import { getFullName } from 'src/Libs/utils/string-functions'
import { UserData } from 'src/State/User/UserModel'
import { useTypedSelector } from 'src/State/RootReducer'
import CompanyPermissionsField from '../components/CompanyUsersPermissionFields'
import { SERVICE_LEVELS } from '../../../Configuration/user'

enum ReasonParam {
  NOTIFICATION = 'notification',
  SHOP = 'shop',
}

interface HeaderData {
  label: string
  key: string
}

interface UseCompanyUserTableParams {
  users: CompanyInstallerTableItemModel[]
  companyId: number
  onChangeShop: (value: boolean, userId: number) => Promise<void>
  onChangeNotifications: (value: boolean, userId: number) => Promise<void>
  onReSendInvite: (companyId: number, token: string) => void
  companyHasShop: boolean
  companyManager?: number
}

interface UseCompanyUserTableReturnValue {
  /**
   * @param companyId
   */
  data: React.ReactNode[][]
  header: HeaderData[]
}

const DEFAULT_NO_DATA = '-'

/**
 * Provides pre-build table items and header information for the provided user composition models
 */
const useCompanyUserTableData = ({
  companyHasShop,
  companyId,
  onChangeNotifications,
  onChangeShop,
  onReSendInvite,
  users,
  companyManager,
}: UseCompanyUserTableParams): UseCompanyUserTableReturnValue => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const userLevel = useTypedSelector(store => store.user.userData?.level) || 'USER'

  const buildDisplayNameCell = (item: CompanyInstallerTableItemModel): string | ReactNode => {
    if (!item) {
      return DEFAULT_NO_DATA
    }

    if (item?.isInvite) {
      return item.displayName
    }

    return (
      <LinkButton
        clickable={!!SERVICE_LEVELS.includes(userLevel) && !item.isInvite}
        content={getFullName((item.value as UserData).Vorname, (item.value as UserData).Name)}
        hasUnderline={false}
        link={`/overview/users/${item.id}`}
        linkColor={theme.palette.primary.main}
      />
    )
  }

  const buildEmailCell = (item: CompanyInstallerTableItemModel): string | undefined => {
    return item.email
  }

  const buildRoleDisplayNameCell = (item: CompanyInstallerTableItemModel): string => {
    let role = item.role
    if (companyManager === item?.id) role = t('service-dashboard.company.manager')
    return role
  }

  const buildStateCell = useCallback(
    (item: CompanyInstallerTableItemModel): React.ReactNode => {
      if (item.isInvite) return t('user.company.invitation.invited')
      return t('service-dashboard.company.users.validity')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onReSendInvite, companyId]
  )

  const buildPermissionCell = (item: CompanyInstallerTableItemModel): React.ReactNode => {
    const model = item?.value as CompanySettingsUserDataModel
    const hasNotificationAccess = model.mailNotification
    const hasShopAccess = Boolean(model?.permissions?.useShop)
    const disableShopAccess = !companyHasShop || item.isInvite
    const disableNotification = item.isInvite

    const getDisableReason = (key: ReasonParam): string => {
      const reasonKey = item.isInvite ? 'Invited' : 'Disable'
      return t(`service-dashboard.company.${key}Reason${reasonKey}`)
    }

    return (
      <CompanyPermissionsField
        hasNotificationAccess={hasNotificationAccess}
        onToggleNotificationAccess={onChangeNotifications}
        hasShopAccess={hasShopAccess}
        disableShopAccess={disableShopAccess}
        disableNotification={disableNotification}
        onToggleShopAccess={onChangeShop}
        userId={model.ID}
        disableNotificationReason={getDisableReason(ReasonParam.NOTIFICATION)}
        disableShopReason={getDisableReason(ReasonParam.SHOP)}
      />
    )
  }

  const header = [
    'user.company.table.userDisplayName',
    'common.email',
    'user.company.table.userLevel',
    'user.company.table.inviteStatus',
    'user.company.table.permissions',
  ].map(elem => ({
    label: t(elem),
    key: elem,
  }))

  const data = useMemo<React.ReactNode[][]>(() => {
    return users.map(userItem => {
      const tableCells = []
      tableCells.push(buildDisplayNameCell(userItem), buildEmailCell(userItem), buildRoleDisplayNameCell(userItem), buildStateCell(userItem))
      tableCells.push(buildPermissionCell(userItem))
      return tableCells
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, companyManager])

  return { header, data }
}

export default useCompanyUserTableData
